<template>
  <div class="finalization-editor">
    <div class="editor-container">
      <div class="editor-header">
        <h3>Viimeistele arviointi</h3>
        <p class="editor-subtitle">
          Muokkaa valittua arviointia ja viimeistele se lähetettäväksi
          opiskelijalle.
        </p>
      </div>

      <div class="tiptap-editor-wrapper">
        <div class="wysiwyg-editor">
          <!-- Tiptap Toolbar -->
          <div class="editor-toolbar" v-if="editor">
            <!-- Text Formatting -->
            <div class="toolbar-group">
              <button
                @click="editor.chain().focus().toggleBold().run()"
                :class="{ 'is-active': editor.isActive('bold') }"
                class="toolbar-button"
                title="Bold (Ctrl+B)"
              >
                <strong>B</strong>
              </button>
              <button
                @click="editor.chain().focus().toggleItalic().run()"
                :class="{ 'is-active': editor.isActive('italic') }"
                class="toolbar-button"
                title="Italic (Ctrl+I)"
              >
                <em>I</em>
              </button>
              <button
                @click="editor.chain().focus().toggleUnderline().run()"
                :class="{ 'is-active': editor.isActive('underline') }"
                class="toolbar-button"
                title="Underline (Ctrl+U)"
              >
                <u>U</u>
              </button>
              <button
                @click="editor.chain().focus().toggleStrike().run()"
                :class="{ 'is-active': editor.isActive('strike') }"
                class="toolbar-button"
                title="Strikethrough"
              >
                <s>S</s>
              </button>
            </div>

            <!-- Headings -->
            <div class="toolbar-group">
              <button
                @click="
                  editor.chain().focus().toggleHeading({ level: 1 }).run()
                "
                :class="{
                  'is-active': editor.isActive('heading', { level: 1 }),
                }"
                class="toolbar-button"
                title="Heading 1"
              >
                H1
              </button>
              <button
                @click="
                  editor.chain().focus().toggleHeading({ level: 2 }).run()
                "
                :class="{
                  'is-active': editor.isActive('heading', { level: 2 }),
                }"
                class="toolbar-button"
                title="Heading 2"
              >
                H2
              </button>
              <button
                @click="
                  editor.chain().focus().toggleHeading({ level: 3 }).run()
                "
                :class="{
                  'is-active': editor.isActive('heading', { level: 3 }),
                }"
                class="toolbar-button"
                title="Heading 3"
              >
                H3
              </button>
            </div>

            <!-- Lists -->
            <div class="toolbar-group">
              <button
                @click="editor.chain().focus().toggleBulletList().run()"
                :class="{ 'is-active': editor.isActive('bulletList') }"
                class="toolbar-button"
                title="Bullet List"
              >
                •
              </button>
              <button
                @click="editor.chain().focus().toggleOrderedList().run()"
                :class="{ 'is-active': editor.isActive('orderedList') }"
                class="toolbar-button"
                title="Ordered List"
              >
                1.
              </button>
              <button
                @click="editor.chain().focus().toggleBlockquote().run()"
                :class="{ 'is-active': editor.isActive('blockquote') }"
                class="toolbar-button"
                title="Quote"
              >
                "
              </button>
            </div>

            <!-- Text Alignment -->
            <div class="toolbar-group">
              <button
                @click="editor.chain().focus().setTextAlign('left').run()"
                :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }"
                class="toolbar-button"
                title="Align Left"
              >
                ⇤
              </button>
              <button
                @click="editor.chain().focus().setTextAlign('center').run()"
                :class="{
                  'is-active': editor.isActive({ textAlign: 'center' }),
                }"
                class="toolbar-button"
                title="Align Center"
              >
                ⇔
              </button>
              <button
                @click="editor.chain().focus().setTextAlign('right').run()"
                :class="{
                  'is-active': editor.isActive({ textAlign: 'right' }),
                }"
                class="toolbar-button"
                title="Align Right"
              >
                ⇥
              </button>
            </div>

            <!-- Formatting -->
            <div class="toolbar-group">
              <button
                @click="editor.chain().focus().toggleHighlight().run()"
                :class="{ 'is-active': editor.isActive('highlight') }"
                class="toolbar-button"
                title="Highlight"
              >
                ⬛
              </button>
              <button
                @click="editor.chain().focus().toggleCode().run()"
                :class="{ 'is-active': editor.isActive('code') }"
                class="toolbar-button"
                title="Code"
              >
                &lt;&gt;
              </button>
              <button
                @click="editor.chain().focus().toggleCodeBlock().run()"
                :class="{ 'is-active': editor.isActive('codeBlock') }"
                class="toolbar-button"
                title="Code Block"
              >
                {}
              </button>
            </div>

            <!-- Actions -->
            <div class="toolbar-group">
              <button
                @click="editor.chain().focus().undo().run()"
                :disabled="!editor.can().undo()"
                class="toolbar-button"
                title="Undo (Ctrl+Z)"
              >
                ↶
              </button>
              <button
                @click="editor.chain().focus().redo().run()"
                :disabled="!editor.can().redo()"
                class="toolbar-button"
                title="Redo (Ctrl+Y)"
              >
                ↷
              </button>
            </div>

            <!-- Links and Media -->
            <div class="toolbar-group">
              <button
                @click="addLink"
                :class="{ 'is-active': editor.isActive('link') }"
                class="toolbar-button"
                title="Add Link"
              >
                🔗
              </button>
              <button
                @click="addImage"
                class="toolbar-button"
                title="Add Image"
              >
                🖼️
              </button>
            </div>
          </div>

          <editor-content :editor="editor" class="tiptap-editor" />
        </div>
      </div>

      <div class="editor-footer">
        <div class="editor-stats">
          <span>{{ wordCount }} sanaa · {{ charCount }} merkkiä</span>
        </div>
        <div class="editor-actions">
          <button
            class="preview-button"
            @click="togglePreview"
            :class="{ active: isPreviewMode }"
          >
            {{ isPreviewMode ? "Muokkaa" : "Esikatselu" }}
          </button>
          <button
            class="submit-button"
            @click="submitReview"
            :disabled="!canSubmit || props.isSubmitting"
          >
            {{
              props.isSubmitting
                ? "Lähetetään..."
                : "Lähetä arviointi opiskelijalle"
            }}
          </button>
        </div>
      </div>
    </div>

    <!-- Preview Modal -->
    <div
      v-if="isPreviewMode"
      class="preview-modal-overlay"
      @click="togglePreview"
    >
      <div class="preview-modal" @click.stop>
        <div class="preview-header">
          <span class="preview-title">Esikatselu</span>
          <button class="close-button" @click="togglePreview">×</button>
        </div>
        <div class="preview-content">
          <div class="document-body" v-html="previewHtml"></div>
        </div>
        <div class="preview-footer">
          <button class="close-preview-button" @click="togglePreview">
            Sulje esikatselu
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { Editor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "tiptap-markdown";
import { marked } from "marked";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import Color from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import Typography from "@tiptap/extension-typography";

const props = defineProps<{
  initialContent?: string;
  disabled?: boolean;
  isSubmitting?: boolean;
}>();

const emit = defineEmits<{
  contentChange: [content: string];
  submitReview: [content: string];
}>();

const editor = ref<Editor | null>(null);
const isPreviewMode = ref(false);

// Initialize editor
onMounted(() => {
  editor.value = new Editor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
        // Disable the default Link extension from StarterKit since we're adding a custom one
        link: false,
      }),
      Markdown.configure({
        html: true, // Allow HTML rendering for WYSIWYG mode
        tightLists: true, // Render lists properly
        bulletListMarker: "-", // Use dashes for bullet lists
        linkify: true, // Auto-convert URLs to links
        breaks: true, // Convert line breaks to <br>
        transformPastedText: true, // Transform pasted content
        transformCopiedText: true, // Transform copied content
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "editor-link",
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: "editor-image",
        },
      }),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Highlight.configure({
        multicolor: false,
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Underline,
      Placeholder.configure({
        placeholder: "Aloita kirjoittamaan arviointia...",
      }),
      Color,
      TextStyle,
      Typography,
    ],
    content: "", // Start empty, will be set by watch
    editable: !props.disabled,
    editorProps: {
      attributes: {
        class: "prosemirror-editor",
      },
      handleKeyDown: (view, event) => {
        // Handle custom keyboard shortcuts
        if (event.ctrlKey || event.metaKey) {
          switch (event.key) {
            case "b":
              event.preventDefault();
              editor.value?.chain().focus().toggleBold().run();
              return true;
            case "i":
              event.preventDefault();
              editor.value?.chain().focus().toggleItalic().run();
              return true;
            case "u":
              event.preventDefault();
              editor.value?.chain().focus().toggleUnderline().run();
              return true;
            case "k":
              event.preventDefault();
              addLink();
              return true;
            case "Enter":
              event.preventDefault();
              editor.value?.chain().focus().setHardBreak().run();
              return true;
          }
        }

        // Handle Alt/Option shortcuts
        if (event.altKey) {
          switch (event.key) {
            case "1":
              event.preventDefault();
              editor.value?.chain().focus().toggleHeading({ level: 1 }).run();
              return true;
            case "2":
              event.preventDefault();
              editor.value?.chain().focus().toggleHeading({ level: 2 }).run();
              return true;
            case "3":
              event.preventDefault();
              editor.value?.chain().focus().toggleHeading({ level: 3 }).run();
              return true;
          }
        }

        return false;
      },
    },
    onUpdate: ({ editor }) => {
      // Get markdown from storage for content change events
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
      const content =
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
        (editor.storage.markdown as any)?.getMarkdown?.() || editor.getHTML();
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const contentStr = content as string;
      console.log(
        "📝 FinalizationEditor content updated, emitting contentChange:",
        {
          contentLength: contentStr.length,
          content:
            contentStr.substring(0, 100) +
            (contentStr.length > 100 ? "..." : ""),
        },
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      emit("contentChange", content);
    },
  });
});

// Cleanup editor
onBeforeUnmount(() => {
  if (editor.value) {
    editor.value.destroy();
  }
});

// Watch for content changes from parent
watch(
  () => props.initialContent,
  (newContent) => {
    console.log("📝 FinalizationEditor received initial content:", {
      contentLength: newContent?.length || 0,
      content:
        (newContent?.substring(0, 100) || "") +
        (newContent && newContent.length > 100 ? "..." : ""),
      editorReady: !!editor.value,
    });

    // If editor is ready, set content immediately
    if (newContent !== undefined && editor.value) {
      editor.value.commands.setContent(newContent);
      console.log("📝 FinalizationEditor content set in editor immediately");
    } else if (newContent !== undefined) {
      // If editor is not ready yet, wait for it to be ready
      console.log("📝 FinalizationEditor waiting for editor to be ready...");
      const trySetContent = () => {
        if (editor.value) {
          editor.value.commands.setContent(newContent);
          console.log("📝 FinalizationEditor content set in editor after wait");
        } else {
          // Try again in the next tick
          setTimeout(trySetContent, 10);
        }
      };
      trySetContent();
    }
  },
  { immediate: true },
);

// Watch for disabled state changes
watch(
  () => props.disabled,
  (disabled) => {
    if (editor.value) {
      editor.value.setEditable(!disabled);
    }
  },
);

// Computed properties for stats
const wordCount = computed(() => {
  const text = editor.value?.getText() || "";
  return text.trim() ? text.trim().split(/\s+/).length : 0;
});

const charCount = computed(() => {
  return editor.value?.getText().length || 0;
});

const canSubmit = computed(() => {
  return wordCount.value > 0 && !props.disabled;
});

const previewHtml = computed(() => {
  const content = getCurrentContent();
  return marked(content);
});

// Helper function to get current content
const getCurrentContent = () => {
  if (editor.value) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
    const content =
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
      (editor.value.storage.markdown as any)?.getMarkdown?.() ||
      editor.value.getHTML();
    return content as string;
  }
  return "";
};

// Methods
const togglePreview = () => {
  isPreviewMode.value = !isPreviewMode.value;
};

const submitReview = () => {
  if (!canSubmit.value) return;

  // Render the content as HTML for the final submit
  const content = previewHtml.value;
  emit("submitReview", content);
};

// Toolbar helper methods
const addLink = () => {
  if (!editor.value) return;

  const url = window.prompt("Syötä linkki:");
  if (url) {
    editor.value.chain().focus().setLink({ href: url }).run();
  }
};

const addImage = () => {
  if (!editor.value) return;

  const url = window.prompt("Syötä kuvan URL:");
  if (url) {
    editor.value.chain().focus().setImage({ src: url }).run();
  }
};
</script>

<style scoped>
.finalization-editor {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.editor-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.editor-header {
  margin-bottom: 20px;
}

.editor-header h3 {
  margin: 0 0 8px 0;
  color: #2c3e50;
  font-weight: 600;
}

.editor-subtitle {
  margin: 0;
  color: #7f8c8d;
  font-size: 0.9rem;
}

.tiptap-editor-wrapper {
  flex: 1;
  border: 2px solid #e1e8ed;
  border-radius: 8px;
  overflow: hidden;
  min-height: 300px;
}

.wysiwyg-editor {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 16px;
  background: linear-gradient(to bottom, #ffffff, #f8f9fa);
  border-bottom: 2px solid #e1e8ed;
  align-items: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 10;
}

.toolbar-group {
  display: flex;
  gap: 3px;
  padding: 0 12px;
  border-right: 2px solid #e1e8ed;
  position: relative;
}

.toolbar-group:last-child {
  border-right: none;
}

.toolbar-group:before {
  content: "";
  position: absolute;
  right: -1px;
  top: 25%;
  height: 50%;
  width: 1px;
  background: linear-gradient(to bottom, transparent, #dee2e6, transparent);
}

.toolbar-group:last-child:before {
  display: none;
}

.toolbar-button {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.toolbar-button:before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
    circle at center,
    rgba(0, 122, 255, 0.1) 0%,
    transparent 70%
  );
  opacity: 0;
  transition: opacity 0.15s ease;
}

.toolbar-button:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #007aff;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 122, 255, 0.15);
}

.toolbar-button:hover:not(:disabled):before {
  opacity: 1;
}

.toolbar-button:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 1px 3px rgba(0, 122, 255, 0.2);
}

.toolbar-button.is-active {
  background: linear-gradient(135deg, #007aff, #5856d6);
  color: white;
  border-color: #007aff;
  box-shadow: 0 2px 8px rgba(0, 122, 255, 0.3);
  transform: translateY(-1px);
}

.toolbar-button.is-active:hover {
  background: linear-gradient(135deg, #0056b3, #4c46c7);
  box-shadow: 0 3px 12px rgba(0, 122, 255, 0.4);
}

.toolbar-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.toolbar-button:disabled:hover {
  background: white;
  border-color: #dee2e6;
  transform: none;
  box-shadow: none;
}

/* Special styling for specific button types */
.toolbar-button strong {
  font-weight: 800;
  font-size: 15px;
}

.toolbar-button em {
  font-style: italic;
  font-size: 15px;
}

.toolbar-button u {
  text-decoration: underline;
  text-underline-offset: 2px;
}

.toolbar-button s {
  text-decoration: line-through;
  text-decoration-thickness: 2px;
}

/* Responsive design for smaller screens */
@media (max-width: 768px) {
  .editor-toolbar {
    padding: 12px 8px;
    gap: 4px;
  }

  .toolbar-group {
    padding: 0 6px;
  }

  .toolbar-button {
    min-width: 32px;
    height: 32px;
    padding: 6px 8px;
    font-size: 13px;
  }
}

.tiptap-editor {
  flex: 1;
  padding: 16px;
  outline: none;
  overflow-y: auto;
}

/* Tiptap content styling */
.tiptap-editor :deep(.ProseMirror) {
  outline: none;
  min-height: 100%;
  line-height: 1.6;
  font-family:
    -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.tiptap-editor :deep(.ProseMirror h1) {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 1rem 0 0.5rem 0;
  color: #2c3e50;
}

.tiptap-editor :deep(.ProseMirror h2) {
  font-size: 1.3rem;
  font-weight: 600;
  margin: 1rem 0 0.5rem 0;
  color: #2c3e50;
}

.tiptap-editor :deep(.ProseMirror h3) {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0.8rem 0 0.4rem 0;
  color: #2c3e50;
}

.tiptap-editor :deep(.ProseMirror p) {
  margin: 0.5rem 0;
}

.tiptap-editor :deep(.ProseMirror ul),
.tiptap-editor :deep(.ProseMirror ol) {
  padding-left: 1.5rem;
  margin: 0.5rem 0;
}

.tiptap-editor :deep(.ProseMirror li) {
  margin: 0.2rem 0;
}

.tiptap-editor :deep(.ProseMirror blockquote) {
  border-left: 4px solid #3498db;
  padding-left: 1rem;
  margin: 1rem 0;
  color: #5a6c7d;
  font-style: italic;
}

.tiptap-editor :deep(.ProseMirror code) {
  background: #f1f2f6;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-family: "Monaco", "Menlo", monospace;
  font-size: 0.9em;
}

.tiptap-editor :deep(.ProseMirror pre) {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 1rem;
  overflow-x: auto;
  margin: 1rem 0;
}

.tiptap-editor :deep(.ProseMirror pre code) {
  background: none;
  padding: 0;
}

.tiptap-editor :deep(.ProseMirror strong) {
  font-weight: 700;
}

.tiptap-editor :deep(.ProseMirror em) {
  font-style: italic;
}

.tiptap-editor :deep(.ProseMirror u) {
  text-decoration: underline;
}

.tiptap-editor :deep(.ProseMirror s) {
  text-decoration: line-through;
}

.tiptap-editor :deep(.ProseMirror mark) {
  background: #ffeb3b;
  color: #000;
  padding: 0.1em 0.2em;
  border-radius: 2px;
}

.tiptap-editor :deep(.editor-link) {
  color: #007aff;
  text-decoration: underline;
  cursor: pointer;
}

.tiptap-editor :deep(.editor-link:hover) {
  color: #0056b3;
}

.tiptap-editor :deep(.editor-image) {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  margin: 0.5rem 0;
}

.tiptap-editor :deep(.ProseMirror[style*="text-align: center"]) {
  text-align: center;
}

.tiptap-editor :deep(.ProseMirror[style*="text-align: right"]) {
  text-align: right;
}

.tiptap-editor :deep(.ProseMirror[style*="text-align: left"]) {
  text-align: left;
}

/* Table styles */
.tiptap-editor :deep(.ProseMirror table) {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin: 1rem 0;
  overflow: hidden;
}

.tiptap-editor :deep(.ProseMirror table td),
.tiptap-editor :deep(.ProseMirror table th) {
  min-width: 1em;
  border: 1px solid #ced4da;
  padding: 8px;
  vertical-align: top;
  box-sizing: border-box;
  position: relative;
}

.tiptap-editor :deep(.ProseMirror table th) {
  font-weight: bold;
  text-align: left;
  background-color: #f8f9fa;
}

/* Placeholder styling */
.tiptap-editor :deep(.ProseMirror p.is-editor-empty:first-child::before) {
  content: attr(data-placeholder);
  float: left;
  color: #adb5bd;
  pointer-events: none;
  height: 0;
}

.editor-footer {
  margin-top: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid #e1e8ed;
}

.editor-stats {
  color: #7f8c8d;
  font-size: 0.85rem;
}

.editor-actions {
  display: flex;
  gap: 12px;
}

.preview-button {
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.preview-button:hover {
  background: #5a6268;
}

.preview-button.active {
  background: #007aff;
}

.submit-button {
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.4);
}

.submit-button:disabled {
  background: #6c757d;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Preview modal styles */
.preview-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.preview-modal {
  background: white;
  border-radius: 12px;
  max-width: 800px;
  max-height: 80vh;
  width: 90vw;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.preview-header {
  padding: 20px 24px 16px;
  border-bottom: 1px solid #e1e8ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.preview-title {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9ca3af;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  background: #f8f9fa;
  color: #495057;
}

.preview-content {
  flex: 1;
  padding: 32px 40px;
  overflow-y: auto;
  background: #f7f6f3;
}

.preview-content :deep(.document-body) {
  background: white;
  max-width: 620px;
  margin: 0 auto;
  padding: 40px 48px;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  font-family: Georgia, "Times New Roman", serif;
  font-size: 1rem;
  line-height: 1.7;
  color: #1a1a1a;
}

.preview-content :deep(p) {
  margin: 0 0 1em;
}

.preview-content :deep(h1),
.preview-content :deep(h2),
.preview-content :deep(h3) {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  margin: 1.4em 0 0.4em;
  line-height: 1.3;
  color: #2c3e50;
}

.preview-content :deep(h1) {
  font-size: 1.4rem;
}
.preview-content :deep(h2) {
  font-size: 1.2rem;
}
.preview-content :deep(h3) {
  font-size: 1.05rem;
}

.preview-content :deep(ul),
.preview-content :deep(ol) {
  margin: 0 0 1em 1.4em;
  padding: 0;
}

.preview-content :deep(li) {
  margin-bottom: 0.3em;
}

.preview-content :deep(strong) {
  font-weight: 700;
}

.preview-content :deep(hr) {
  border: none;
  border-top: 1px solid #e1e8ed;
  margin: 1.5em 0;
}

.preview-footer {
  padding: 16px 24px;
  border-top: 1px solid #e1e8ed;
  text-align: center;
}

.close-preview-button {
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 20px;
  cursor: pointer;
  font-size: 0.9rem;
}

.close-preview-button:hover {
  background: #5a6268;
}
</style>
